import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalDataType } from '../types/api';
import { apiServices } from '../services/api';
import { useCacheConfig } from '../contexts/cache-config-context';

// External Clients hooks
export const useExternalClients = () => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['external-data', 'clients'],
    queryFn: () => apiServices.externalData.getExternalClients(),
    staleTime: getCacheTime('externalClients'),
  });
};

export const useExternalClient = (id: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['external-data', 'clients', id],
    queryFn: () => apiServices.externalData.getExternalClientById(id),
    staleTime: getCacheTime('externalClients'),
    enabled: !!id,
  });
};

export const useSearchExternalClients = (query: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['external-data', 'clients', 'search', query],
    queryFn: () => apiServices.externalData.searchExternalClients(query),
    staleTime: getCacheTime('externalClients'),
    enabled: !!query && query.length >= 2,
  });
};

// External Articles hooks
export const useExternalArticles = () => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['external-data', 'articles'],
    queryFn: () => apiServices.externalData.getExternalArticles(),
    staleTime: getCacheTime('externalArticles'),
  });
};

export const useExternalArticle = (id: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['external-data', 'articles', id],
    queryFn: () => apiServices.externalData.getExternalArticleById(id),
    staleTime: getCacheTime('externalArticles'),
    enabled: !!id,
  });
};

export const useSearchExternalArticles = (query: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['external-data', 'articles', 'search', query],
    queryFn: () => apiServices.externalData.searchExternalArticles(query),
    staleTime: getCacheTime('externalArticles'),
    enabled: !!query && query.length >= 2,
  });
};

// Sync hooks
export const useSyncExternalData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => apiServices.externalData.syncAllExternalData(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-data'] });
    },
  });
};

export const useExternalDataStats = () => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['external-data', 'stats'],
    queryFn: () => apiServices.externalData.getExternalDataStats(),
    staleTime: getCacheTime('externalData'),
  });
};

// Legacy placeholder hooks per retrocompatibilità
export const useAllExternalData = () => {
  return useExternalClients();
};

export const useExternalData = (id: string) => {
  return useExternalClient(id);
};

export const useExternalDataByType = (type: ExternalDataType) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['external-data', 'type', type],
    queryFn: () => apiServices.externalData.getExternalClients(), // Default fallback
    staleTime: getCacheTime('externalData'),
  });
};

export const useCreateExternalData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => Promise.resolve(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-data'] });
    },
  });
};

export const useUpdateExternalData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => Promise.resolve({ id, ...data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-data'] });
    },
  });
};

export const useDeleteExternalData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => Promise.resolve({ deleted: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-data'] });
    },
  });
};
